import { List, Map, Set } from "immutable";

import syncApplications from "../../../constants/syncApplications";
import immutableRecords from "../../../types/immutableRecords";
import referenceSelectors from "../../definition/references/selectors";
import { actionTypes as syncActionTypes } from "../actions";
import { actionTypes } from "./actions";
import { defaultScrivenerFields, defaultUlyssesFields } from "./defaultSettings";

const updateFields = (state, action, globalState) => {
  switch (action.type) {
    case actionTypes.CONFIGURE_SYNC_FIELD:
      return state.set(
        action.id,
        immutableRecords.SyncFieldSettingsRecord({
          id: action.id,
          customFieldId: action.customFieldId
        })
      );
    case actionTypes.REMOVE_SYNC_FIELD:
      return state.delete(action.id);
    case actionTypes.SET_USE_KEYWORDS_FOR_RELATIONSHIPS: {
      if (action.useKeywords) {
        // clear out any fields for references
        return state.filter((field, id) => !referenceSelectors.getReferencesById(globalState).has(id));
      }
      return state;
    }
    default:
      return state;
  }
};

const updateLevels = (levels, index, fieldName, typeId) => {
  if (index < levels.size) {
    return levels.setIn([index, fieldName], typeId);
  }
  let initialData = {};
  initialData[fieldName] = typeId;
  return levels.push(immutableRecords.SyncFolderLevelSettingsRecord(initialData));
};

const updateFolders = (state, action) => {
  switch (action.type) {
    case actionTypes.ADD_ENTITY_FOLDER:
      return !state.has(action.id)
        ? state.set(action.id, immutableRecords.SyncEntityFolderSettingsRecord({ folderId: action.id }))
        : state;
    case actionTypes.REMOVE_ENTITY_FOLDER:
      return state.delete(action.id);
    case actionTypes.SET_MAIN_TYPE_FOR_ENTITY_FOLDER:
      // When they change the main folder type, we reset everything else
      return state.update(action.folderId, f =>
        f.merge({
          mainType: action.typeId,
          defaultDocumentType: action.typeId,
          defaultFolderType: action.typeId,
          flattened: true,
          levels: List()
        })
      );
    case actionTypes.SET_FLATTENED_FOR_ENTITY_FOLDER:
      // When they change the main folder type, we reset everything else
      return state.update(action.folderId, f => f.set("flattened", action.flattened));
    case actionTypes.SET_DEFAULT_TYPE_FOR_DOCUMENT_IN_ENTITY_FOLDER:
      return state.update(action.folderId, f => f.set("defaultDocumentType", action.typeId));
    case actionTypes.SET_DEFAULT_TYPE_FOR_FOLDER_IN_ENTITY_FOLDER:
      return state.update(action.folderId, f => f.set("defaultFolderType", action.typeId));
    case actionTypes.SET_TYPE_FOR_DOCUMENT_AT_LEVEL_OF_ENTITY_FOLDER:
      return state.updateIn([action.folderId, "levels"], levels =>
        updateLevels(levels, action.level, "documentType", action.typeId)
      );
    case actionTypes.SET_TYPE_FOR_FOLDER_AT_LEVEL_OF_ENTITY_FOLDER:
      return state.updateIn([action.folderId, "levels"], levels =>
        updateLevels(levels, action.level, "folderType", action.typeId)
      );
    case actionTypes.ADD_LEVEL_OF_ENTITY_FOLDER: {
      const mainType = state.get(action.folderId).mainType;
      // either duplicate the last entry, or fill with mainType
      return state.updateIn([action.folderId, "levels"], levels =>
        levels.push(
          levels.size
            ? levels.last()
            : immutableRecords.SyncFolderLevelSettingsRecord({
              documentType: mainType,
              folderType: mainType
            })
        )
      );
    }
    case actionTypes.REMOVE_LEVEL_OF_ENTITY_FOLDER:
      return state.updateIn([action.folderId, "levels"], levels => levels.delete(action.level));
    default:
      return state;
  }
};

const reducer = (state = immutableRecords.SyncSettingsRecord(), action, globalState) => {
  switch (action.type) {
    case actionTypes.ENABLE_SYNC:
      return state.merge({
        enabled: action.enabled,
        path: action.enabled ? state.path : null,
        fields: action.enabled ? state.fields : Map(),
        entityFolders: action.enabled ? state.entityFolders : Map(),
        keywordTypes: action.enabled ? state.keywordTypes : Set(),
        useKeywordsForRelationships: action.enabled ? state.useKeywordsForRelationships : true,
        narrativeFolderId: action.enabled ? state.narrativeFolderId : null
      });
    case actionTypes.SELECT_APPLICATION:
      return state.merge({
        app: action.appId,
        enabled: action.appId !== syncApplications.NONE,
        path: action.appId !== syncApplications.NONE ? state.path : null
      });
    case actionTypes.RESET_SYNC_SETTINGS: {
      const defaultFields = action.appId === syncApplications.SCRIVENER ? defaultScrivenerFields : defaultUlyssesFields;
      return state.merge({
        fields: defaultFields,
        entityFolders: Map(),
        keywordTypes: Set(),
        useKeywordsForRelationships: true,
        narrativeFolderId: null
      });
    }
    case actionTypes.SET_PROJECT_PATH:
      return state.set("path", action.path);
    case actionTypes.SET_NARRATIVE_FOLDER_ID:
      return state.set("narrativeFolderId", action.narrativeFolderId);
    case syncActionTypes.UPDATE_EXTERNAL_REP:
      // If we do not have a narrative folder set yet, then default to using the manuscript
      // folder. This is needed so that a v2 sync knows what to do from the start!
      return !state.narrativeFolderId && action.rep && action.rep.narrativeFolderId
        ? state.set("narrativeFolderId", action.rep.narrativeFolderId)
        : state;
    case actionTypes.REPLACE_SETTINGS:
      return action.settings;
    case actionTypes.CONFIGURE_SYNC_FIELD:
    case actionTypes.REMOVE_SYNC_FIELD:
      return state.update("fields", f => updateFields(f, action, globalState));
    case actionTypes.ADD_ENTITY_FOLDER:
    case actionTypes.REMOVE_ENTITY_FOLDER:
    case actionTypes.SET_MAIN_TYPE_FOR_ENTITY_FOLDER:
    case actionTypes.SET_FLATTENED_FOR_ENTITY_FOLDER:
    case actionTypes.SET_DEFAULT_TYPE_FOR_DOCUMENT_IN_ENTITY_FOLDER:
    case actionTypes.SET_DEFAULT_TYPE_FOR_FOLDER_IN_ENTITY_FOLDER:
    case actionTypes.SET_TYPE_FOR_DOCUMENT_AT_LEVEL_OF_ENTITY_FOLDER:
    case actionTypes.SET_TYPE_FOR_FOLDER_AT_LEVEL_OF_ENTITY_FOLDER:
    case actionTypes.ADD_LEVEL_OF_ENTITY_FOLDER:
    case actionTypes.REMOVE_LEVEL_OF_ENTITY_FOLDER:
      return state.update("entityFolders", f => updateFolders(f, action));
    case actionTypes.SET_USE_KEYWORDS_FOR_RELATIONSHIPS: {
      if (action.useKeywords) {
        return state
          .set("useKeywordsForRelationships", action.useKeywords)
          .update("fields", f => updateFields(f, action, globalState));
      }
      // otherwise clear keywords out
      return state.set("useKeywordsForRelationships", action.useKeywords).set("keywordTypes", Set());
    }
    case actionTypes.ADD_KEYWORD_TYPE:
      return state.update("keywordTypes", types => types.add(action.typeId));
    case actionTypes.REMOVE_KEYWORD_TYPE:
      return state.update("keywordTypes", types => types.delete(action.typeId));
    default:
      return state;
  }
};

export default reducer;
